import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';
import { useLanguage } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MarketingContactPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t('contact.form.completeAll'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/contact`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        message: form.message.trim(),
      });
      toast.success(t('contact.form.success'));
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('contact.form.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    borderRadius: 14,
    border: '1px solid var(--mkt-card-border-strong)',
    background: 'var(--mkt-card-bg)',
    fontFamily: "'Manrope', sans-serif",
    fontSize: 15,
    color: 'var(--mkt-text)',
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <MarketingLayout>
      <section style={{ maxWidth: 1180, margin: '0 auto', padding: '72px 22px 80px' }}>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ margin: 0, fontSize: 12, letterSpacing: 1.6, textTransform: 'uppercase', color: '#E53935', fontWeight: 700 }}
        >
          {t('contact.eyebrow')}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.05 }}
          data-testid="marketing-contact-title"
          style={{
            margin: '14px 0 0',
            fontFamily: "'Inria Serif', serif",
            fontSize: 'clamp(2.4rem, 5vw, 4.2rem)',
            lineHeight: 1,
            letterSpacing: '-0.02em',
            color: 'var(--mkt-text)',
          }}
        >
          {t('contact.title')}
        </motion.h1>
        <p style={{ margin: '22px 0 0', maxWidth: 540, fontSize: 17, lineHeight: 1.6, color: 'var(--mkt-text-muted)' }}>
          {t('contact.subtitle')}
        </p>

        <div
          style={{
            marginTop: 44,
            display: 'grid',
            gap: 28,
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            alignItems: 'start',
          }}
          className="marketing-contact-grid"
        >
          <form
            onSubmit={handleSubmit}
            data-testid="marketing-contact-form"
            style={{
              background: 'var(--mkt-card-bg)',
              border: '1px solid var(--mkt-card-border)',
              borderRadius: 28,
              padding: 28,
              boxShadow: 'var(--mkt-card-shadow)',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div>
              <label htmlFor="contact-name" style={{ display: 'block', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--mkt-text-subtle)', fontWeight: 700, marginBottom: 8 }}>
                {t('contact.form.name')}
              </label>
              <input
                id="contact-name"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                placeholder={t('contact.form.namePlaceholder')}
                data-testid="marketing-contact-name-input"
                style={inputStyle}
              />
            </div>
            <div>
              <label htmlFor="contact-email" style={{ display: 'block', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--mkt-text-subtle)', fontWeight: 700, marginBottom: 8 }}>
                {t('contact.form.email')}
              </label>
              <input
                id="contact-email"
                type="email"
                value={form.email}
                onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                placeholder={t('contact.form.emailPlaceholder')}
                data-testid="marketing-contact-email-input"
                style={inputStyle}
              />
            </div>
            <div>
              <label htmlFor="contact-message" style={{ display: 'block', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--mkt-text-subtle)', fontWeight: 700, marginBottom: 8 }}>
                {t('contact.form.message')}
              </label>
              <textarea
                id="contact-message"
                rows={5}
                value={form.message}
                onChange={(event) => setForm((current) => ({ ...current, message: event.target.value }))}
                placeholder={t('contact.form.messagePlaceholder')}
                data-testid="marketing-contact-message-input"
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="marketing-contact-submit-button"
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--mkt-cta-solid-text)',
                background: 'var(--mkt-cta-solid-bg)',
                border: 'none',
                borderRadius: 9999,
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1,
                padding: '14px 24px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                marginTop: 6,
              }}
            >
              {submitting ? t('contact.form.submitting') : (<>{t('contact.form.submit')} <Send size={16} /></>)}
            </button>
          </form>

          <aside
            style={{
              background: 'linear-gradient(160deg, #161127 0%, #2A1B5A 100%)',
              color: '#FFFFFF',
              borderRadius: 28,
              padding: 28,
              display: 'flex',
              flexDirection: 'column',
              gap: 24,
            }}
            data-testid="marketing-contact-sidebar"
          >
            <div>
              <p style={{ margin: 0, fontSize: 12, letterSpacing: 1.6, textTransform: 'uppercase', color: '#E53935', fontWeight: 700 }}>
                {t('contact.aside.eyebrow')}
              </p>
              <h3 style={{ margin: '10px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 26, letterSpacing: '-0.01em' }}>
                {t('contact.aside.title')}
              </h3>
            </div>

            <a
              href="mailto:contact@allurexp.com"
              data-testid="marketing-contact-card-email"
              style={{ color: '#FFFFFF', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 14, fontFamily: "'Manrope', sans-serif", fontSize: 16, fontWeight: 600 }}
            >
              <span style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Mail size={18} color="#E53935" />
              </span>
              contact@allurexp.com
            </a>

            <a
              href="tel:+306943963295"
              data-testid="marketing-contact-card-phone-1"
              style={{ color: '#FFFFFF', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 14, fontFamily: "'Manrope', sans-serif", fontSize: 16, fontWeight: 600 }}
            >
              <span style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Phone size={18} color="#E53935" />
              </span>
              +30 694 396 3295
            </a>

            <a
              href="tel:+306943963291"
              data-testid="marketing-contact-card-phone-2"
              style={{ color: '#FFFFFF', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 14, fontFamily: "'Manrope', sans-serif", fontSize: 16, fontWeight: 600 }}
            >
              <span style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Phone size={18} color="#E53935" />
              </span>
              +30 694 396 3291
            </a>

            <p style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontSize: 13, lineHeight: 1.6, color: '#B1A7CC' }}>
              {t('contact.aside.reply')}
            </p>
          </aside>
        </div>
        <style>{`
          @media (max-width: 760px) {
            .marketing-contact-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </section>
    </MarketingLayout>
  );
}
